import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { db, auth as firebaseAuth } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { colors, spacing, borderRadius, shadows, typography } from '../theme/colors';
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiDollarSign, FiPackage, FiShoppingCart, FiUsers, FiFileText, FiTrendingUp, FiBookOpen, FiLayers } from 'react-icons/fi';
import SwitcherLogo from '../LOGO-SHORT.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' });
  const [focusedField, setFocusedField] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const userCredential = await login(email, password);
      const user = userCredential.user;

      // Offline-authenticated session: skip the pendingSignups Firestore check
      // (it would fail because Firestore can't reach the server). An offline
      // login by definition means a returning user — they've signed up before,
      // so the pendingSignups gate isn't applicable.
      if (userCredential._offline) {
        navigate('/dashboard');
        return;
      }

      // Online path: check pendingSignups for the email-verification flow.
      // Wrap in try/catch because Firestore can fail transiently — we don't
      // want a flaky network read to block a successful login.
      let pendingDoc;
      try {
        pendingDoc = await getDoc(doc(db, 'pendingSignups', user.uid));
      } catch (e) {
        console.warn('Could not check pendingSignups (network):', e?.message);
        pendingDoc = null;
      }

      if (pendingDoc && pendingDoc.exists()) {
        // Signup not finished yet (a pending record still exists). Send them to
        // the signup screen to finish — verify by WhatsApp or email, or start
        // over. We do NOT sign them out: that was the old dead-end where an
        // unverified user could never get back in. Verification is a one-time
        // signup gate and no company exists yet, so /signup is exactly where
        // they can clear it (or bail out). Works for both verified-but-not-yet-
        // created and not-yet-verified accounts.
        navigate('/signup');
        return;
      }

      navigate('/dashboard');
    } catch (error) {
      let errorMessage = 'Failed to login. Please check your credentials.';

      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled. Contact support.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (typeof error.code === 'string' && error.code.startsWith('auth/offline-')) {
        // Use the message AuthContext built for the specific offline-failure mode
        errorMessage = error.message || errorMessage;
      }

      setError(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setResetMessage({ type: 'error', text: 'Please enter your email address.' });
      return;
    }
    setResetLoading(true);
    setResetMessage({ type: '', text: '' });
    try {
      await sendPasswordResetEmail(firebaseAuth, resetEmail);
      setResetMessage({ type: 'success', text: 'Password reset link sent! Check your email inbox.' });
    } catch (err) {
      if (err.code === 'auth/invalid-email') {
        setResetMessage({ type: 'error', text: 'Please enter a valid email address.' });
      } else if (err.code === 'auth/too-many-requests') {
        setResetMessage({ type: 'error', text: 'Too many requests. Please try again later.' });
      } else {
        setResetMessage({ type: 'success', text: 'If an account exists with this email, a reset link has been sent.' });
      }
    } finally {
      setResetLoading(false);
    }
  };

  const modules = [
    { icon: <FiBookOpen />, label: 'Accounting' },
    { icon: <FiPackage />, label: 'Inventory' },
    { icon: <FiShoppingCart />, label: 'Point of Sale' },
    { icon: <FiFileText />, label: 'Invoicing' },
    { icon: <FiUsers />, label: 'HR & Payroll' },
    { icon: <FiDollarSign />, label: 'Finance' },
    { icon: <FiTrendingUp />, label: 'Reports' },
    { icon: <FiLayers />, label: 'Multi-Branch' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  const formVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: {
      opacity: 1, x: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], staggerChildren: 0.08, delayChildren: 0.3 }
    }
  };

  const formItemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  return (
    <Container>
      {/* Animated background elements */}
      <BgOrb style={{ top: '10%', left: '5%' }} as={motion.div} animate={{ y: [0, -30, 0], x: [0, 15, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }} />
      <BgOrb style={{ bottom: '15%', right: '8%' }} as={motion.div} animate={{ y: [0, 20, 0], x: [0, -20, 0] }} transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }} $size="200px" $opacity="0.08" />
      <BgOrb style={{ top: '50%', left: '50%' }} as={motion.div} animate={{ y: [0, -15, 0], scale: [1, 1.1, 1] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }} $size="120px" $opacity="0.05" />

      {/* Grid pattern overlay */}
      <GridOverlay />

      <LoginCard as={motion.div} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}>

        {/* Left Brand Panel */}
        <LeftPanel as={motion.div} variants={containerVariants} initial="hidden" animate="visible">
          <LeftContent>
            {/* Logo */}
            <motion.div variants={itemVariants}>
              <LogoWrapper>
                <LogoText>AMAL<LogoHighlight>ERP</LogoHighlight></LogoText>
                <LogoLine as={motion.div} initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.8, delay: 0.6 }} />
              </LogoWrapper>
            </motion.div>

            {/* Tagline */}
            <motion.div variants={itemVariants}>
              <Tagline>Complete Business Management</Tagline>
              <TaglineBold>Accounting & ERP Software</TaglineBold>
              <TaglineSub>All-in-one solution for accounting, inventory, POS, invoicing, payroll and more — built for Pakistani businesses</TaglineSub>
            </motion.div>

            {/* Module pills */}
            <motion.div variants={itemVariants}>
              <ModuleSectionLabel>Everything you need in one place</ModuleSectionLabel>
            </motion.div>
            <ModuleGrid variants={containerVariants}>
              {modules.map((mod, i) => (
                <ModulePill key={i} as={motion.div} variants={itemVariants} whileHover={{ scale: 1.05, background: 'rgba(255,255,255,0.18)', transition: { duration: 0.2 } }}>
                  <ModulePillIcon>{mod.icon}</ModulePillIcon>
                  {mod.label}
                </ModulePill>
              ))}
            </ModuleGrid>

            {/* Stats */}
            <motion.div variants={itemVariants}>
              <StatsRow>
                <StatItem>
                  <StatNumber>500+</StatNumber>
                  <StatLabel>Businesses</StatLabel>
                </StatItem>
                <StatDivider />
                <StatItem>
                  <StatNumber>99.9%</StatNumber>
                  <StatLabel>Uptime</StatLabel>
                </StatItem>
                <StatDivider />
                <StatItem>
                  <StatNumber>24/7</StatNumber>
                  <StatLabel>Support</StatLabel>
                </StatItem>
              </StatsRow>
            </motion.div>
          </LeftContent>
        </LeftPanel>

        {/* Right Form Panel */}
        <RightPanel>
          <FormWrapper as={motion.div} variants={formVariants} initial="hidden" animate="visible">
            <AnimatePresence mode="wait">
              {showForgotPassword ? (
                <motion.div key="forgot" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                  <FormHeader>
                    <WelcomeIcon as={motion.div} animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}>
                      <FiMail size={28} />
                    </WelcomeIcon>
                    <Title>Reset Password</Title>
                    <Subtitle>Enter your email to receive a reset link</Subtitle>
                  </FormHeader>

                  {resetMessage.text && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                      {resetMessage.type === 'success'
                        ? <SuccessMessage>{resetMessage.text}</SuccessMessage>
                        : <ErrorMessage>{resetMessage.text}</ErrorMessage>
                      }
                    </motion.div>
                  )}

                  <Form onSubmit={handleForgotPassword}>
                    <InputGroup>
                      <Label>Email Address</Label>
                      <InputWrapper $focused={focusedField === 'resetEmail'}>
                        <IconWrapper $focused={focusedField === 'resetEmail'}>
                          <FiMail />
                        </IconWrapper>
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          onFocus={() => setFocusedField('resetEmail')}
                          onBlur={() => setFocusedField(null)}
                        />
                      </InputWrapper>
                    </InputGroup>

                    <SubmitButton as={motion.button} type="submit" disabled={resetLoading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      {resetLoading ? <Spinner /> : <>Send Reset Link <FiArrowRight /></>}
                    </SubmitButton>
                  </Form>

                  <BottomLink>
                    <BackLink onClick={() => { setShowForgotPassword(false); setResetMessage({ type: '', text: '' }); }}>
                      Back to Sign In
                    </BackLink>
                  </BottomLink>
                </motion.div>
              ) : (
                <motion.div key="login" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                  <FormHeader>
                    <WelcomeIcon as={motion.div} animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}>
                      <FiTrendingUp size={28} />
                    </WelcomeIcon>
                    <Title>Welcome Back</Title>
                    <Subtitle>Sign in to manage your business</Subtitle>
                  </FormHeader>

                  <AnimatePresence>
                    {error && (
                      <motion.div initial={{ opacity: 0, y: -10, height: 0 }} animate={{ opacity: 1, y: 0, height: 'auto' }} exit={{ opacity: 0, y: -10, height: 0 }}>
                        <ErrorMessage>{error}</ErrorMessage>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <Form onSubmit={handleSubmit}>
                    <motion.div variants={formItemVariants}>
                      <InputGroup>
                        <Label>Email Address</Label>
                        <InputWrapper $focused={focusedField === 'email'}>
                          <IconWrapper $focused={focusedField === 'email'}>
                            <FiMail />
                          </IconWrapper>
                          <Input
                            type="email"
                            placeholder="you@company.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onFocus={() => setFocusedField('email')}
                            onBlur={() => setFocusedField(null)}
                          />
                        </InputWrapper>
                      </InputGroup>
                    </motion.div>

                    <motion.div variants={formItemVariants}>
                      <InputGroup>
                        <LabelRow>
                          <Label>Password</Label>
                          <ForgotLink onClick={() => { setShowForgotPassword(true); setResetEmail(email); setError(''); }}>
                            Forgot?
                          </ForgotLink>
                        </LabelRow>
                        <InputWrapper $focused={focusedField === 'password'}>
                          <IconWrapper $focused={focusedField === 'password'}>
                            <FiLock />
                          </IconWrapper>
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onFocus={() => setFocusedField('password')}
                            onBlur={() => setFocusedField(null)}
                          />
                          <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <FiEyeOff /> : <FiEye />}
                          </PasswordToggle>
                        </InputWrapper>
                      </InputGroup>
                    </motion.div>

                    <motion.div variants={formItemVariants}>
                      <SubmitButton as={motion.button} type="submit" disabled={loading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        {loading ? <Spinner /> : <>Sign In <FiArrowRight /></>}
                      </SubmitButton>
                    </motion.div>
                  </Form>

                  <motion.div variants={formItemVariants}>
                    <BottomLink>
                      Don't have an account? <Link to="/signup">Create Account</Link>
                    </BottomLink>
                  </motion.div>

                  <motion.div variants={formItemVariants}>
                    <CompanyBranding>
                      <CompanyBrandText>Powered by <strong>AMAL ERP</strong></CompanyBrandText>
                    </CompanyBranding>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </FormWrapper>
        </RightPanel>
      </LoginCard>

      <Footer as={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
        AMALERP &mdash; Pakistan's Complete Accounting & ERP Software
      </Footer>
    </Container>
  );
};

// ─── Keyframes ────────────────────────────────────────────────
const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ─── Styled Components ────────────────────────────────────────
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(-45deg, #1a0008, ${colors.primary.dark}, #2a0012, #0d0d0d);
  background-size: 400% 400%;
  animation: ${gradientShift} 15s ease infinite;
  padding: ${spacing.lg};
  position: relative;
  overflow: hidden;
`;

const GridOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(128, 0, 32, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(128, 0, 32, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
`;

const BgOrb = styled.div`
  position: absolute;
  width: ${props => props.$size || '150px'};
  height: ${props => props.$size || '150px'};
  border-radius: 50%;
  background: radial-gradient(circle, ${colors.primary.main}, transparent);
  opacity: ${props => props.$opacity || '0.06'};
  filter: blur(40px);
  pointer-events: none;
`;

const LoginCard = styled.div`
  display: flex;
  width: 100%;
  max-width: 1100px;
  background: rgba(255, 255, 255, 0.97);
  border-radius: 20px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05);
  overflow: hidden;
  min-height: 620px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    min-height: auto;
  }
`;

const LeftPanel = styled.div`
  flex: 1.15;
  background: linear-gradient(160deg, ${colors.primary.dark} 0%, ${colors.primary.main} 40%, ${colors.primary.light} 100%);
  padding: 48px 40px;
  color: ${colors.text.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -30%;
    width: 80%;
    height: 80%;
    background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -20%;
    left: -20%;
    width: 60%;
    height: 60%;
    background: radial-gradient(circle, rgba(0,0,0,0.15) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const LeftContent = styled.div`
  position: relative;
  z-index: 1;
`;

const LogoWrapper = styled.div`
  display: inline-block;
  margin-bottom: 24px;
`;

const LogoText = styled.h1`
  font-size: 48px;
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 4px;
  line-height: 1;
  margin: 0;
  font-family: ${typography.fontFamily.heading};
  color: #FFFFFF;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 36px;
    letter-spacing: 3px;
  }
`;

const LogoHighlight = styled.span`
  color: #FFD700;
`;

const LogoLine = styled.div`
  height: 3px;
  background: linear-gradient(90deg, #FFD700, rgba(255, 215, 0, 0.2));
  border-radius: 2px;
  margin-top: 6px;
`;

const Tagline = styled.p`
  font-size: 15px;
  font-weight: ${typography.fontWeight.regular};
  opacity: 0.7;
  margin-bottom: 2px;
  line-height: 1.3;
  letter-spacing: 0.3px;
`;

const TaglineBold = styled.p`
  font-size: 22px;
  font-weight: ${typography.fontWeight.bold};
  margin-bottom: 10px;
  line-height: 1.3;
  font-family: ${typography.fontFamily.heading};
`;

const TaglineSub = styled.p`
  font-size: 13px;
  opacity: 0.55;
  line-height: 1.6;
  margin-bottom: 24px;
  max-width: 360px;
`;

const ModuleSectionLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  opacity: 0.5;
  margin-bottom: 12px;
  font-weight: ${typography.fontWeight.medium};
`;

const ModuleGrid = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 28px;
`;

const ModulePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: ${typography.fontWeight.medium};
  cursor: default;
  white-space: nowrap;
  transition: all 0.2s;
`;

const ModulePillIcon = styled.span`
  display: flex;
  align-items: center;
  font-size: 14px;
  opacity: 0.85;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 16px 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 20px;
  font-weight: ${typography.fontWeight.bold};
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 11px;
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 30px;
  background: rgba(255, 255, 255, 0.15);
`;

// ─── Right Panel ──────────────────────────────────────────────
const RightPanel = styled.div`
  flex: 1;
  padding: 48px 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 380px;
`;

const FormHeader = styled.div`
  margin-bottom: 28px;
`;

const WelcomeIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${colors.primary.main}15, ${colors.primary.light}20);
  color: ${colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 26px;
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
  margin-bottom: 6px;
  font-family: ${typography.fontFamily.heading};
`;

const Subtitle = styled.p`
  color: ${colors.text.secondary};
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  background: ${colors.status.error}10;
  color: ${colors.status.error};
  padding: 12px 14px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 13px;
  border: 1px solid ${colors.status.error}25;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '!';
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${colors.status.error};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const SuccessMessage = styled.div`
  background: ${colors.status.success}10;
  color: ${colors.status.success};
  padding: 12px 14px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 13px;
  border: 1px solid ${colors.status.success}25;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary};
`;

const ForgotLink = styled.span`
  font-size: 12px;
  color: ${colors.primary.main};
  cursor: pointer;
  font-weight: ${typography.fontWeight.medium};
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.light};
    text-decoration: underline;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border: 1.5px solid ${props => props.$focused ? colors.primary.main : colors.border.light};
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${props => props.$focused ? `${colors.primary.main}04` : '#FAFAFA'};

  ${props => props.$focused && css`
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  `}
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 14px;
  color: ${props => props.$focused ? colors.primary.main : colors.text.tertiary};
  display: flex;
  align-items: center;
  font-size: 17px;
  transition: color 0.3s;
`;

const Input = styled.input`
  width: 100%;
  padding: 13px 14px 13px 42px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  background: transparent;
  color: ${colors.text.primary};
  outline: none;

  &::placeholder {
    color: ${colors.text.disabled};
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 14px;
  background: none;
  border: none;
  color: ${colors.text.tertiary};
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 17px;
  padding: 0;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.main};
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%);
  color: ${colors.text.white};
  font-size: 15px;
  font-weight: ${typography.fontWeight.semibold};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
    background-size: 200% 100%;
    animation: ${shimmer} 3s infinite;
  }

  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px ${colors.primary.main}40;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const BottomLink = styled.p`
  text-align: center;
  margin-top: 24px;
  font-size: 13px;
  color: ${colors.text.secondary};

  a {
    color: ${colors.primary.main};
    font-weight: ${typography.fontWeight.semibold};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const BackLink = styled.span`
  color: ${colors.primary.main};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  margin-top: 20px;
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  letter-spacing: 0.5px;
  z-index: 1;
`;

const CompanyBranding = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid ${colors.border.light};
`;

const CompanyLogoImg = styled.img`
  width: 120px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
`;

const CompanyBrandText = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  letter-spacing: 0.3px;

  strong {
    color: ${colors.text.secondary};
    font-weight: ${typography.fontWeight.semibold};
  }
`;

export default Login;
